import { shownClock } from './round-config'
import type { JobTimeHow } from './types'

// Job times (migration 0020): when a worker tapped Start and Finish on a
// visit. Stored as SQLite UTC text ('YYYY-MM-DD HH:MM:SS'); shown in London
// time, because that is the clock on the van's dashboard.
//
// Pure functions only, so the lines below are the same wherever a visit is
// shown (the worker's Today, the owner's diary, a customer's history) and are
// tested without a page.

export interface JobTimes {
  started_at?: string | null
  finished_at?: string | null
  // How each was got (0083): a rough or unknown one is said so on screen and left out of every figure.
  started_how?: JobTimeHow | null
  finished_how?: JobTimeHow | null
  // Outsides done, doing insides (0085).
  outsides_done_at?: string | null
}

// Minutes each side of Outsides done (0085), when it sits between a start and a finish (or now, while under way).
export function splitMinutes(cl: JobTimes, now: Date = new Date()): { outside: number; inside: number | null } | null {
  if (!cl.outsides_done_at || !cl.started_at || cl.outsides_done_at < cl.started_at) return null
  if (cl.finished_at && cl.outsides_done_at > cl.finished_at) return null
  const outside = minutesBetween(cl.started_at, cl.outsides_done_at)
  return { outside, inside: cl.finished_at ? minutesBetween(cl.outsides_done_at, cl.finished_at) : null }
}

// A time said to be rough, or not known (Astra's late-start design, 22 Oct 2026): no measure of the work. The server's
// tiers leave the same ones out (functions/lib/tiers.ts).
const UNCOUNTED: ReadonlySet<string> = new Set(['rough', 'unsure', 'guessed'])
export function countsAsTimed(cl: JobTimes): boolean {
  return !!cl.started_at && !!cl.finished_at && !UNCOUNTED.has(cl.started_how ?? '') && !UNCOUNTED.has(cl.finished_how ?? '')
}

// '2026-09-23 08:12:40' (UTC) → '09:12' on the round's clock (London for every UK round, BST in
// summer; round-config.ts, Step 1).
export function clockOf(utc: string): string {
  return shownClock(new Date(`${utc.replace(' ', 'T')}Z`))
}

// Whole minutes between two stamps, never negative.
export function minutesBetween(a: string, b: string): number {
  const ms = Date.parse(`${b.replace(' ', 'T')}Z`) - Date.parse(`${a.replace(' ', 'T')}Z`)
  return Math.max(0, Math.round(ms / 60000))
}

// '34 min', '1 hr 5 min', '2 hr'.
export function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h} hr ${m} min` : `${h} hr`
}

// The one line a visit shows: 'Started 09:12' while the job is under way,
// 'Arrived 09:12 · took 34 min' once it is finished, null with no start. A time said to be rough reads "about"; one not
// known says so instead of a number (0083).
export function jobTimesLine(cl: JobTimes): string | null {
  if (!cl.started_at) return null
  const startUnsure = cl.started_how === 'unsure'
  // Rough, or Roundly's booking guess saved as it was: "about".
  const about = (how: JobTimeHow | null | undefined): string => (how === 'rough' || how === 'guessed' ? 'about ' : '')
  // Doing the insides (0085): since when, and how long the outsides took.
  if (!cl.finished_at && splitMinutes(cl) && !startUnsure) return `Insides since ${clockOf(cl.outsides_done_at!)} · outsides took ${about(cl.started_how)}${durationLabel(splitMinutes(cl)!.outside)}`
  if (!cl.finished_at) return startUnsure ? 'Under way · start time not known' : `Started ${about(cl.started_how)}${clockOf(cl.started_at)}`
  if (startUnsure) return `Finished ${about(cl.finished_how)}${clockOf(cl.finished_at)} · time taken not known`
  if (cl.finished_how === 'unsure') return `Arrived ${about(cl.started_how)}${clockOf(cl.started_at)} · time taken not known`
  const rough = !!about(cl.started_how) || !!about(cl.finished_how)
  // Timed apart (0085): "Arrived 11:04am · outsides 32 min · insides 24 min".
  const split = splitMinutes(cl)
  if (split && split.inside !== null) return `Arrived ${about(cl.started_how)}${clockOf(cl.started_at)} · outsides ${rough ? 'about ' : ''}${durationLabel(split.outside)} · insides ${rough ? 'about ' : ''}${durationLabel(split.inside)}`
  return `Arrived ${about(cl.started_how)}${clockOf(cl.started_at)} · took ${rough ? 'about ' : ''}${durationLabel(minutesBetween(cl.started_at, cl.finished_at))}`
}

// The phone's own moment, as the server stores it. The PATCH carries the ISO
// form (with its zone); the visit object on screen is updated with this one.
export function nowStamps(now: Date = new Date()): { iso: string; stored: string } {
  const iso = now.toISOString()
  return { iso, stored: iso.slice(0, 19).replace('T', ' ') }
}

// A customer's timed visits, for the Tier and job times box on their Analysis (src/job-times-card.ts; Liam, 13 Oct
// 2026: "average time taken ... also what each worker took on this job"). Only visits done with Start and Finish both
// recorded, neither rough nor not known (0083), and 5 minutes to 8 hours apart count, as the tiers count them
// (functions/lib/tiers.ts). The usual time is the
// last three whole jobs without insides, or with insides when every timed job had them; a part done (fronts only)
// counts only as the last timed visit, never in an average.
export interface TimedVisit extends JobTimes {
  id?: number
  state: string
  date: string
  worker?: string | null
  deleted_at?: string | null
  part?: string | null
  inside_pence?: number | null
  extras_json?: string | null
}
export interface JobTimesFacts {
  timed: number
  usual: number | null
  usualWithInsides: boolean
  // Usual is the first clean's time: nothing else is timed yet (Liam, 21 Oct 2026: "if there is no regular clean time data
  // then this can go in there"). Shown in a soft warning colour, explained on hover or tap.
  usualFromFirst: boolean
  // Every clean the time above is from is the first clean (an ad hoc customer with nothing else timed).
  usualOnlyFirst: boolean
  byWorker: { name: string; minutes: number; jobs: number }[]
  last: { date: string; minutes: number; part: string | null } | null
  // Their first clean when it was timed (0082): one line, "First clean: 1 hr 10 min (21 Oct)". `parts` only when it was
  // part done; `whole` false when a part of it is not finished and timed, so the minutes are that part's alone.
  first: { date: string; minutes: number; whole: boolean; parts: { part: string; minutes: number }[] } | null
}

// A taken-on customer's first clean (0082, functions/lib/first-clean.ts): the ids of its visits (removed ones included)
// and its first visit's day, from the server, and whether it counts in the usual time (only for a customer with no
// repeat: planning.ts noFixedCycle).
export interface FirstClean { ids: number[]; date?: string; counts: boolean }

const MIN_TIMED = 5
const MAX_TIMED = 8 * 60

export function jobTimesFacts(visits: TimedVisit[], firstClean: FirstClean | null = null): JobTimesFacts {
  const timed = visits
    .filter((v) => v.state === 'done' && !v.deleted_at && countsAsTimed(v))
    .map((v) => ({ v, m: minutesBetween(v.started_at!, v.finished_at!) }))
    .filter((x) => x.m >= MIN_TIMED && x.m <= MAX_TIMED)
    .sort((a, b) => `${b.v.date} ${b.v.finished_at}`.localeCompare(`${a.v.date} ${a.v.finished_at}`))
  const inFirst = (v: TimedVisit): boolean => !!firstClean && v.id !== undefined && firstClean.ids.includes(v.id)
  // The usual time leaves the first clean out unless it counts (a customer with no repeat); who did it and the last
  // timed job still show every timed visit.
  // A visit with extras (0087) is not their ordinary job: its minutes include the extra work (Astra's extras check 12).
  const whole = timed.filter((x) => !x.v.part && !x.v.extras_json && (firstClean?.counts || !inFirst(x.v)))
  const hasInsides = (v: TimedVisit): boolean => !!v.inside_pence && v.inside_pence > 0
  // A whole job with insides timed apart (0085) gives its outsides time too, as the tiers take it.
  const outsides = whole.flatMap((x) => {
    if (!hasInsides(x.v)) return [x]
    const sp = splitMinutes(x.v)
    return sp && sp.inside !== null && sp.inside >= 1 && sp.outside >= MIN_TIMED && sp.outside <= MAX_TIMED ? [{ v: x.v, m: sp.outside }] : []
  })
  const usualFrom = (outsides.length ? outsides : whole).slice(0, 3)
  const average = (ms: number[]): number => Math.round(ms.reduce((s, m) => s + m, 0) / ms.length)
  const groups = new Map<string, number[]>()
  // Who does it: the same cleans as the time above it, so a long first clean never makes a worker look slow (Astra on
  // the pictures); with nothing else timed, the first clean.
  const allWhole = timed.filter((t) => !t.v.part)
  for (const x of whole.length ? whole : allWhole) {
    const name = x.v.worker?.trim() || 'Not named'
    groups.set(name, [...(groups.get(name) ?? []), x.m])
  }
  const byWorker = [...groups].map(([name, ms]) => ({ name, minutes: average(ms), jobs: ms.length }))
    .sort((a, b) => b.jobs - a.jobs || a.name.localeCompare(b.name))
  // The first clean's own line: its visits still on the page (a removed one is not shown), each part finished and timed
  // or the line says only what was.
  const family = firstClean ? visits.filter((v) => !v.deleted_at && inFirst(v)) : []
  const familyTimed = timed.filter((x) => inFirst(x.v))
  // Whole only when every one of its visits is here, done and timed: a removed part leaves the rest "so far", never the
  // whole first clean (GPT-6.1). Its day is its first visit's, from the server.
  const first = familyTimed.length
    ? {
        date: firstClean?.date ?? [...family].sort((a, b) => a.date.localeCompare(b.date))[0]!.date,
        minutes: familyTimed.reduce((sum, x) => sum + x.m, 0),
        whole: !!firstClean && family.length === firstClean.ids.length && family.length === familyTimed.length && family.every((v) => v.state === 'done'),
        parts: familyTimed.some((x) => x.v.part) ? familyTimed.map((x) => ({ part: x.v.part?.trim() || 'the rest', minutes: x.m })).reverse() : [],
      }
    : null
  // Nothing but the first clean timed: Usual is the first clean's time, marked as such, until a regular clean is timed.
  // ...never a first clean that had extras on it: its minutes include the extra work (GPT-6.1's extras review 11).
  const usualFromFirst = !usualFrom.length && !!first && first.whole && !family.some((v) => v.extras_json)
  return {
    timed: timed.length,
    usual: usualFrom.length ? average(usualFrom.map((x) => x.m)) : usualFromFirst ? first!.minutes : null,
    usualWithInsides: !outsides.length && usualFrom.length > 0,
    usualFromFirst,
    usualOnlyFirst: usualFrom.length > 0 && usualFrom.every((x) => inFirst(x.v)),
    byWorker,
    last: timed[0] ? { date: timed[0].v.date, minutes: timed[0].m, part: timed[0].v.part ?? null } : null,
    first,
  }
}
